import { useCallback } from "react";
import * as React from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import TeamPill from "components/team-pill";
import { ALREADY_TAKEN_MESSAGE } from "features/staff-tasks-v2/components/staff-task-modal-dialog/view-staff-task-form/team-task-banner.component";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import { useTakeStaffTaskMutation } from "services/staff-tasks";
import {
  type TaskNotification,
  type TeamTaskNotificationExtra,
  isTeamTaskExtra
} from "services/types/notifications";

/**
 * "Omar A. sent a task to [Chicago North PCCs]" (TEAMS_FEATURE, a team whose alert mode rings the
 * bell): the patient and title, Take it, Open task. Once a teammate takes it the backend marks the
 * row read; the task itself says who has it.
 */
const TeamTaskNotification = ({
  title,
  extra
}: {
  title: string;
  extra: TeamTaskNotificationExtra;
}) => {
  const dispatch = useAppDispatch();
  const { handleOpenTaskModal } = useStaffTasksV2();
  const [takeTask, { isLoading }] = useTakeStaffTaskMutation();
  const {
    staff_task_id,
    staff_task_title,
    patient_name,
    team_name,
    team_color
  } = extra;

  const openTask = useCallback(() => {
    handleOpenTaskModal({ mode: "view", taskId: staff_task_id });
  }, [handleOpenTaskModal, staff_task_id]);

  const handleTake = useCallback(async () => {
    try {
      await takeTask(staff_task_id).unwrap();
      openTask();
    } catch (error) {
      const status =
        typeof error === "object" && error && "status" in error
          ? Number((error as { status: unknown }).status)
          : undefined;
      dispatch(
        setCustomMessage(
          status === 400
            ? ALREADY_TAKEN_MESSAGE
            : "Could not take the task. Try again."
        )
      );
      dispatch(setSnackbarOpen(true));
    }
  }, [dispatch, openTask, staff_task_id, takeTask]);

  return (
    <ListItemText
      primary={
        <Stack
          component="span"
          direction="row"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
        >
          <span>{title}</span>
          <TeamPill name={team_name} color={team_color} />
        </Stack>
      }
      secondary={
        <Stack component="span" gap={0.5}>
          <Typography component="span" variant="body2" color="text.secondary">
            {[patient_name, staff_task_title].filter(Boolean).join(" · ")}
          </Typography>
          <Stack component="span" direction="row" gap={1} alignItems="center">
            <LoadingButton
              size="small"
              variant="contained"
              loading={isLoading}
              onClick={handleTake}
              data-testid="team-task-notification-take"
            >
              Take it
            </LoadingButton>
            <Button
              size="small"
              variant="text"
              onClick={openTask}
              sx={{ minWidth: 0 }}
            >
              Open task
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
};

const TaskNotificationComponent = ({
  title,
  description,
  extra
}: TaskNotification) => {
  const { handleOpenTaskModal } = useStaffTasksV2();
  const legacy = isTeamTaskExtra(extra) ? null : extra;
  const task = legacy?.task;
  const patient_id = legacy?.patient_id;
  const id = legacy?.id;
  const openTaskModalInViewMode = useCallback(() => {
    handleOpenTaskModal({ mode: "view", taskId: task || id });
  }, [handleOpenTaskModal, task, id]);

  if (isTeamTaskExtra(extra)) {
    return <TeamTaskNotification title={title} extra={extra} />;
  }

  return (
    <ListItemText
      primary={title}
      secondary={
        <Stack component="span" gap={0.5}>
          <Typography
            component="span"
            sx={{
              whiteSpace: "pre-line",
              cursor: "pointer",
              "&:hover": { color: "primary.main", textDecoration: "underline" }
            }}
            onClick={openTaskModalInViewMode}
          >
            {description}
          </Typography>
          {patient_id && task && (
            <Link
              href={`/patients/${patient_id}/tasks/?task_id=${task}&comment=${id}`}
              underline="hover"
              onClick={openTaskModalInViewMode}
              sx={{ cursor: "pointer" }}
            >
              View Comments
            </Link>
          )}
        </Stack>
      }
    />
  );
};

export default TaskNotificationComponent;
